import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, TrendingUp, TrendingDown, Percent } from 'lucide-react';
import { buildBulkPriceAdjustPlan, type PriceAdjustProduct } from '@/lib/bulkPriceAdjust';

interface BulkPriceAdjustModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: string[];
  products: PriceAdjustProduct[];
  onSuccess: () => void;
}

type AdjustmentType = 'increase' | 'decrease';

export function BulkPriceAdjustModal({ open, onOpenChange, categories, products, onSuccess }: BulkPriceAdjustModalProps) {
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('increase');
  const [percentage, setPercentage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(false);

  const handleAdjust = async () => {
    const percentValue = parseFloat(percentage);
    if (isNaN(percentValue) || percentValue <= 0 || percentValue > 100) {
      toast.warning('Informe uma porcentagem válida (1-100)');
      return;
    }

    setLoading(true);

    try {
      const plan = buildBulkPriceAdjustPlan({
        products,
        selectedCategory,
        adjustmentType,
        percentValue,
      });

      if (plan.updates.length === 0) {
        toast.warning('Nenhum item encontrado para ajustar');
        setLoading(false);
        return;
      }

      let successCount = 0;
      const chunkSize = 10;

      for (let i = 0; i < plan.updates.length; i += chunkSize) {
        const chunk = plan.updates.slice(i, i + chunkSize);
        await Promise.all(
          chunk.map(async (up) => {
            const payload: any = { price: up.price };
            // Só atualiza o custo se o item tinha custo antes
            if (up.previousCost != null) {
              payload.cost = up.cost;
            }

            const { error: updateError } = await supabase
              .from('products')
              .update(payload)
              .eq('id', up.id);

            if (!updateError) {
              successCount++;
            }
          })
        );
      }

      toast.success(`${successCount} item(ns) atualizado(s) com sucesso!`);
      onSuccess();
      onOpenChange(false);
      setPercentage('');
      setSelectedCategory('all');
    } catch (error) {
      console.error('Error adjusting prices:', error);
      toast.error('Erro ao ajustar preços');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5" />
            Reajustar preços em massa
          </DialogTitle>
          <DialogDescription>
            Aumente ou diminua os preços de venda e custos de todos os itens do seu catálogo em porcentagem.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Category Selection */}
          <div className="space-y-2">
            <Label>Aplicar em</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a categoria" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="all">Todos os itens</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Adjustment Type */}
          <div className="space-y-2">
            <Label>Tipo de ajuste</Label>
            <RadioGroup 
              value={adjustmentType} 
              onValueChange={(v) => setAdjustmentType(v as AdjustmentType)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="increase" id="increase" />
                <Label htmlFor="increase" className="flex items-center gap-1 cursor-pointer">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  Aumentar
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="decrease" id="decrease" />
                <Label htmlFor="decrease" className="flex items-center gap-1 cursor-pointer">
                  <TrendingDown className="h-4 w-4 text-destructive" />
                  Diminuir
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Percentage Input */}
          <div className="space-y-2">
            <Label htmlFor="percentage">Porcentagem (%)</Label>
            <div className="relative">
              <Input
                id="percentage"
                type="number"
                placeholder="Ex: 10"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                min="0.01"
                max="100"
                step="0.01"
                className="pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
            </div>
          </div>

          {/* Preview */}
          {percentage && !isNaN(parseFloat(percentage)) && (
            <div className="p-3 rounded-lg bg-muted/50 text-sm space-y-1">
              <p className="text-muted-foreground">
                {adjustmentType === 'increase' ? '📈' : '📉'} Preços e custos serão {adjustmentType === 'increase' ? 'aumentados' : 'diminuídos'} em <strong>{percentage}%</strong>
                {selectedCategory !== 'all' ? ` na categoria "${selectedCategory}"` : ' em todo o catálogo'}.
              </p>
              <p className="text-xs text-muted-foreground">
                Exemplo: R$ 100,00 → R$ {(100 * (adjustmentType === 'increase' ? 1 + parseFloat(percentage) / 100 : 1 - parseFloat(percentage) / 100)).toFixed(2).replace('.', ',')}
              </p>
              <p className="text-xs text-muted-foreground font-medium pt-1">
                ℹ️ Os custos de compra preenchidos também serão reajustados. Custos em branco permanecerão em branco.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button3D color="gray" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button3D>
          <Button3D onClick={handleAdjust} disabled={loading || !percentage}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Ajustando...
              </>
            ) : (
              'Aplicar reajuste'
            )}
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

